import { useEffect } from 'react'
import { Platform } from 'react-native'

import { useMutation, useQuery } from '@tanstack/react-query'
import { registerUser } from '@zet/analytics'
import { useToast } from '@zet/common/components/toast'
import {
  addBasicDetailsToAddCustomer,
  addEmploymentDetails,
  getAgentBanner,
  getAgentDetailsWF,
  getCriteriaIndexes,
  getOtpToAddCustomer,
  getPrefilledData,
  verifyOtpToAddCustomer,
} from '@zet/customer/src/api/functions'
import { i18n } from '@zet/localization'
import { isApp } from '@zet/utils'
import { HALF_HOUR, ONE_MINUTE, THIRTY_SECONDS } from '@zet/utils/cacheTime'
import { getLoginHash } from '@zet/utils/loginHash'

import {
  GetCiterialIndexesRequest,
  GetPrefilledDataRequest,
  IAgentBanner,
} from '../../types'

export const isAgentDetailsWFQueryEnabled = Platform.OS === 'web'

function useAgentDetailsWF() {
  const queryKey = ['getAgentDetailsWF']
  if (Platform.OS === 'web') {
    const hash = getLoginHash()
    if (hash) {
      queryKey.push(hash)
    }
  }
  const queryData = useQuery(queryKey, getAgentDetailsWF, {
    refetchOnWindowFocus: false,
    staleTime: isApp ? ONE_MINUTE : HALF_HOUR,
    cacheTime: isApp ? ONE_MINUTE : HALF_HOUR,
    enabled: isAgentDetailsWFQueryEnabled,
  })
  useEffect(() => {
    if (queryData.data?.id) {
      registerUser({
        id: queryData.data?.id,
      })
    }
  }, [queryData.data?.id])
  return queryData
}

function useGetAgentBanners({ onecode }: IAgentBanner) {
  const queryKey = ['getAgentBanners']
  const queryData = useQuery(queryKey, () => getAgentBanner({ onecode }), {
    enabled: !!onecode,
    refetchOnWindowFocus: false,
    cacheTime: THIRTY_SECONDS,
    staleTime: THIRTY_SECONDS,
  })

  return {
    ...queryData,
    isLoading: queryData.isLoading && queryData.fetchStatus !== 'idle',
  }
}

function useGetOtpToAddCustomer() {
  const toast = useToast()

  return useMutation(getOtpToAddCustomer, {
    onSuccess: (data) => {
      if (data?.lockedTill && data?.reason === 'Too many attempts') {
        toast?.show({
          intent: 'error',
          heading: data?.reason,
          hideAfter: 3000,
          description: data?.lockedTill,
        })
      }
    },
    onError: (error) => {
      console.warn('error', error)
    },
  })
}

function useVerifyOtpToAddCustomer() {
  return useMutation(verifyOtpToAddCustomer)
}

function useAddBasicDetailsToAddCustomer() {
  return useMutation(addBasicDetailsToAddCustomer)
}

function useGetPrefilledData(suppliedData: GetPrefilledDataRequest) {
  return useQuery(
    ['getPrefilledData', suppliedData.customerId],
    () => getPrefilledData(suppliedData),
    {
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
    },
  )
}

function useGetCriteriaIndexes(suppliedData: GetCiterialIndexesRequest) {
  const locale = i18n.locale
  return useQuery(
    ['getCriteriaIndexes', locale, suppliedData],
    () => getCriteriaIndexes(suppliedData),
    { refetchOnWindowFocus: false },
  )
}

function useAddEmploymentDetails() {
  return useMutation(addEmploymentDetails)
}

export {
  useAddBasicDetailsToAddCustomer,
  useAddEmploymentDetails,
  useAgentDetailsWF,
  useGetAgentBanners,
  useGetCriteriaIndexes,
  useGetOtpToAddCustomer,
  useGetPrefilledData,
  useVerifyOtpToAddCustomer,
}
